import { Button, Input, ModalV2 } from '@components'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

export type PeriodDialogMode = 'asOf' | 'range'

type Props = {
  isOpen: boolean
  mode: PeriodDialogMode
  initialAsOf?: string
  initialFrom?: string
  initialTo?: string
  onClose: () => void
  onApply: (value: { asOf?: string; dateFrom?: string; dateTo?: string }) => void
}

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 8px;
`

const ErrorText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.error};
`

export const ReportPeriodDialog: React.FC<Props> = ({
  isOpen,
  mode,
  initialAsOf,
  initialFrom,
  initialTo,
  onClose,
  onApply
}) => {
  const [t] = useTranslation()
  const [asOf, setAsOf] = useState(initialAsOf || '')
  const [dateFrom, setDateFrom] = useState(initialFrom || '')
  const [dateTo, setDateTo] = useState(initialTo || '')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setAsOf(initialAsOf || '')
    setDateFrom(initialFrom || '')
    setDateTo(initialTo || '')
    setError(null)
  }, [isOpen, initialAsOf, initialFrom, initialTo])

  const handleApply = () => {
    if (mode === 'asOf') {
      if (!asOf) {
        setError(t('reports.interactive.periodDialog.required'))
        return
      }
      onApply({ asOf })
      return
    }

    if (!dateFrom || !dateTo) {
      setError(t('reports.interactive.periodDialog.required'))
      return
    }
    if (dateFrom > dateTo) {
      setError(t('reports.interactive.periodDialog.invalidRange'))
      return
    }
    onApply({ dateFrom, dateTo })
  }

  return (
    <ModalV2
      isVisible={isOpen}
      handleOnClose={onClose}
      header={
        mode === 'asOf'
          ? t('reports.interactive.periodDialog.asOfTitle')
          : t('reports.interactive.periodDialog.rangeTitle')
      }
      width={420}
      autoHeight
      footer={
        <>
          <Button type="button" onClick={onClose}>
            {t('general.cancel')}
          </Button>
          <Button type="button" intent="primary" onClick={handleApply}>
            {t('reports.interactive.periodDialog.apply')}
          </Button>
        </>
      }
    >
      <Fields>
        {mode === 'asOf' ? (
          <Input
            id="interactive-period-asof"
            type="date"
            label={t('reports.interactive.asOfDate')}
            value={asOf}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAsOf(event.currentTarget.value)
              setError(null)
            }}
          />
        ) : (
          <>
            <Input
              id="interactive-period-from"
              type="date"
              label={t('reports.startDate')}
              value={dateFrom}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setDateFrom(event.currentTarget.value)
                setError(null)
              }}
            />
            <Input
              id="interactive-period-to"
              type="date"
              label={t('reports.endDate')}
              value={dateTo}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setDateTo(event.currentTarget.value)
                setError(null)
              }}
            />
          </>
        )}
        {error && <ErrorText>{error}</ErrorText>}
      </Fields>
    </ModalV2>
  )
}
